/**
 * PastCyclesScreen
 *
 * Asks if the user has done any fertility treatments before.
 * This significantly changes the plan — experienced users
 * skip education steps and get different recommendations.
 */

import React, { useRef, useState } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";

type Props = {
  onNext: () => void;
};

function OptionButton({
  label,
  detail,
  isSelected,
  onPress,
}: {
  label: string;
  detail: string;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror it via the
      // aria-selected prop for deterministic E2E assertions.
      aria-selected={isSelected}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 16,
          gap: 14,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "500",
              color: isSelected ? EMBER.colors.text : EMBER.colors.text,
            }}
          >
            {label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {detail}
          </Text>
        </View>
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            borderWidth: 2,
            borderColor: isSelected ? EMBER.colors.text : EMBER.colors.border,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <View
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: EMBER.colors.text,
              }}
            />
          )}
        </View>
      </Animated.View>
    </Pressable>
  );
}

export default function PastCyclesScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PastCyclesScreen");
  const { state, updateState } = usePlanBuilder();
  const [answer, setAnswer] = useState<boolean | null>(state.hasPastCycles);

  const handleSelect = (value: boolean) => {
    setAnswer(value);
    updateState({ hasPastCycles: value });
    if (!value) updateState({ pastCycles: [] });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.past_cycles.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 28,
        }}
      >
        {t("plan_builder.past_cycles.subtitle")}
      </Text>

      <View style={{ gap: 10 }}>
        <OptionButton
          label={t("plan_builder.past_cycles.yes_label")}
          detail={t("plan_builder.past_cycles.yes_detail")}
          isSelected={answer === true}
          onPress={() => {
            capturePress(
              pressLabel(t("plan_builder.past_cycles.yes_label")),
              "past_cycles_yes",
            );
            handleSelect(true);
          }}
        />
        <OptionButton
          label={t("plan_builder.past_cycles.no_label")}
          detail={t("plan_builder.past_cycles.no_detail")}
          isSelected={answer === false}
          onPress={() => {
            capturePress(
              pressLabel(t("plan_builder.past_cycles.no_label")),
              "past_cycles_no",
            );
            handleSelect(false);
          }}
        />
      </View>

      {answer != null && (
        <View style={{ marginTop: 24 }}>
          <WarmButton
            title={t("plan_builder.continue")}
            onPress={() => onNext()}
          />
        </View>
      )}
    </ScrollView>
  );
}
